/**
 * Single source of truth for turning a `previous_output` input's source step
 * into its display `name` / `description`. Consumed by the draft editor, the
 * recording input dialog, and backend read-time hydration so all three render
 * an identical label for the same edge.
 *
 * Pure and locale-neutral: the appended rule `output` is user-authored free
 * text and the `generated*` fields are already produced in the user's
 * language, so the derivation needs no `t()` and frontend / backend yield
 * byte-identical strings.
 */ import { StepActionTypeSchema } from './step-action-type.schema';
/** Separator joining a source's generated title/description to the rule branch. */ const RULE_BRANCH_SEPARATOR = ' → ';
/** Append the decision branch to a generated base string, skipping empties. */ function appendRuleBranch(base, ruleOutput) {
    const trimmedBase = (base ?? '').trim();
    if (!trimmedBase) return ruleOutput;
    if (!ruleOutput) return trimmedBase;
    return `${trimmedBase}${RULE_BRANCH_SEPARATOR}${ruleOutput}`;
}
/**
 * Derive the `name` / `description` of a `previous_output` step input from the
 * source step it links to.
 *
 * - Event source: the input is fed by the process trigger (a start or
 *   intermediate event node). Events carry no `actionType`/`task`, so their
 *   own `name` / `description` ARE the input's label. Checked first because
 *   `nodeKind === 'event'` is the discriminator; legacy step docs predate
 *   `nodeKind` (`undefined`) and correctly fall through to the `actionType`
 *   branches below.
 * - Task source: the input IS the task's output. `name = task.output`;
 *   `description` prefers the source's generated description, else the output.
 * - Decision source: the input is the branch the chosen rule routed to. Both
 *   the source's generated title (`name`) and generated description get the
 *   chosen rule's `output` appended, so the branch that led here is always
 *   visible. An unresolved `ruleId` (missing, or matching no rule — i.e. a
 *   corrupt or stale link) yields empty strings rather than guessing a branch.
 */ export function deriveStepInputFromSource(source, ruleId) {
    if (source.nodeKind === 'event') {
        return {
            name: source.name ?? '',
            description: source.description ?? ''
        };
    }
    if (source.actionType === StepActionTypeSchema.enum.task) {
        const output = source.task?.output ?? '';
        if (!output) return {
            name: '',
            description: ''
        };
        return {
            name: output,
            description: (source.generatedDescription ?? '').trim() || output
        };
    }
    if (source.actionType === StepActionTypeSchema.enum.decision) {
        if (!ruleId || !source.rules) return {
            name: '',
            description: ''
        };
        const rule = source.rules.find((r)=>r._id != null && String(r._id) === ruleId);
        const ruleOutput = (rule?.output ?? '').trim();
        if (!ruleOutput) return {
            name: '',
            description: ''
        };
        return {
            name: appendRuleBranch(source.generatedName, ruleOutput),
            description: appendRuleBranch(source.generatedDescription, ruleOutput)
        };
    }
    return {
        name: '',
        description: ''
    };
}

//# sourceMappingURL=derive-step-input-source.js.map